import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { getLocations } from '../services/api';

const Locations = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth'
      });
    }
  };

  if (loading) return null;

  return (
    <section className="relative -mt-24 md:-mt-40 pb-20 overflow-hidden z-20">
      <div className="max-w-7xl mx-auto px-4">

        {/* Section Header */}
        <div className="text-center mb-6">
          <h2 className="text-xs md:text-sm font-black uppercase">
            <Link
              to="/locations"
              aria-label="View all bungee jump locations"
              className="text-white hover:text-yellow-400 transition"
            >
              Select Your Jump Location
            </Link>
          </h2>
        </div>

        {/* ================= DESKTOP VIEW ================= */}
        <div className="hidden lg:block relative">
          <div
            ref={scrollContainerRef}
            className="flex justify-center gap-3 overflow-x-auto scrollbar-hide px-12 py-4"
          >
            {locations.map((location) => (
              <button
                key={location.id}
                aria-label={
                  location.comingSoon
                    ? `${location.name} location opening soon`
                    : `Book bungee jump at ${location.name}`
                }
                onClick={() =>
                  !location.comingSoon &&
                  navigate(
                    `/location/${
                      location.slug ||
                      location.name.toLowerCase().replace(/\s+/g, '-')
                    }`
                  )
                }
                className="group relative flex-shrink-0 w-32 h-[200px] rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500 shadow-2xl text-left"
              >
                <img
                    src={location.image}
                    alt={location.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                  />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-3">
                  <h3 className="text-base font-black text-white">
                    {location.name}
                  </h3>

                  {location.comingSoon ? (
                    <span className="inline-block bg-yellow-400 text-black px-2 py-1 rounded-full text-xs font-bold mt-1">
                      Opening Soon
                    </span>
                  ) : (
                    <div className="flex items-center space-x-1 text-yellow-400 text-xs font-bold mt-1">
                      <span>BOOK NOW</span>
                      <ArrowRight size={14} />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ================= MOBILE VIEW ================= */}
        <div className="lg:hidden relative">
          
          {/* Scroll Left */}
          <button
            onClick={() => scroll('left')}
            aria-label="Scroll locations left"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 text-black p-2 rounded-full"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Scroll Right */}
          <button
            onClick={() => scroll('right')}
            aria-label="Scroll locations right"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 text-black p-2 rounded-full"
          >
            <ChevronRight size={18} />
          </button>

          <div
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide px-10 py-4"
          >
            {locations.map((location) => (
              <button
                key={location.id}
                aria-label={
                  location.comingSoon
                    ? `${location.name} location opening soon`
                    : `Book bungee jump at ${location.name}`
                }
                onClick={() =>
                  !location.comingSoon &&
                  navigate(
                    `/location/${
                      location.slug ||
                      location.name.toLowerCase().replace(/\s+/g, '-')
                    }`
                  )
                }
                className="relative flex-shrink-0 w-32 h-[200px] rounded-2xl overflow-hidden shadow-2xl text-left"
              >
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover"
                />

                {/* FIXED Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                {/* FIXED Proper Content Position */}
                <div className="absolute inset-0 flex flex-col justify-end p-3">
                  <h3 className="text-base font-black text-white">
                    {location.name}
                  </h3>

                  {location.comingSoon ? (
                    <span className="bg-yellow-400 text-black px-2 py-1 rounded-full text-xs font-bold mt-1 inline-block">
                      Opening Soon
                    </span>
                  ) : (
                    <div className="flex items-center space-x-1 text-yellow-400 text-xs font-bold mt-1">
                      <span>BOOK NOW</span>
                      <ArrowRight size={14} />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Locations;
